/**
 * Playlist Fetch Service
 *
 * Use-case orchestrator implementing Stale-While-Revalidate (SWR) pattern
 * for fetching playlist data from R2 bucket.
 *
 * Clean Architecture:
 * - Use-case layer: orchestrates caching and fetching
 * - Depends on abstractions (PlaylistCacheGateway) not concretions
 * - Single responsibility: SWR orchestration logic
 *
 * SWR Pattern:
 * 1. Return cached data immediately if available (even if stale)
 * 2. Fetch fresh data in the background
 * 3. Update cache when fresh data arrives
 * 4. Notify caller via callback when background refresh completes
 *
 * Supports ETag/If-None-Match for conditional requests when data hasn't changed.
 *
 * Resilience:
 * - Circuit breaker protection prevents cascading failures
 * - When circuit is open, returns cached data immediately without network request
 */

import { Logger } from '@/app/utils/logger';
import { DataFetchLogger } from '@/app/services/logging/DataFetchLogger';
import { Playlist } from '@/types/MusicPlaylist';
import {
  PlaylistCacheGateway,
  FetchResult,
  FetchOptions,
  OnRefreshCallback,
  ConditionalFetchResult,
  PlaylistNetworkFetcher,
} from '@/types/PlaylistCache';
import {
  getCircuitBreaker,
  CircuitBreakerService,
  CircuitOpenError,
} from '@/app/services/resilience';

const LOG_NAMESPACE = 'playlist-fetch';

/**
 * Dependencies for PlaylistFetchService
 * Using dependency injection for testability and Clean Architecture compliance
 */
export interface PlaylistFetchServiceDeps {
  /** Cache gateway for persistent storage */
  cacheGateway: PlaylistCacheGateway<Playlist>;
  /** Network fetcher for R2 requests */
  networkFetcher: PlaylistNetworkFetcher<Playlist>;
  /** Optional circuit breaker (defaults to shared 'r2-playlist' circuit breaker) */
  circuitBreaker?: CircuitBreakerService;
}

/**
 * SWR fetch result with background refresh tracking
 */
export interface SwrFetchResult extends FetchResult<Playlist> {
  /** Promise that resolves when background refresh completes (if applicable) */
  backgroundRefresh?: Promise<FetchResult<Playlist>>;
  /** Whether the circuit breaker prevented network request */
  circuitOpen?: boolean;
}

/**
 * PlaylistFetchService
 *
 * Implements the Stale-While-Revalidate pattern:
 * - Returns cached data immediately (even if stale) for instant UI
 * - Fetches fresh data in background
 * - Updates cache with fresh data
 * - Notifies via callback when refresh completes
 *
 * Resilience:
 * - Circuit breaker protection prevents cascading failures
 * - When circuit is open, returns cached data immediately
 */
export class PlaylistFetchService {
  private cacheGateway: PlaylistCacheGateway<Playlist>;
  private networkFetcher: PlaylistNetworkFetcher<Playlist>;
  private circuitBreaker: CircuitBreakerService;
  private isFetching: boolean = false;
  private currentFetchPromise: Promise<FetchResult<Playlist>> | null = null;

  constructor(deps: PlaylistFetchServiceDeps) {
    this.cacheGateway = deps.cacheGateway;
    this.networkFetcher = deps.networkFetcher;
    // Use provided circuit breaker or get the shared 'r2-playlist' instance
    this.circuitBreaker = deps.circuitBreaker ?? getCircuitBreaker('r2-playlist');
  }

  /**
   * Fetch playlist using SWR pattern
   *
   * @param options - Fetch options (forceRefresh, skipCache)
   * @param onRefresh - Optional callback when background refresh completes
   * @returns FetchResult with data, source, and staleness info
   */
  async fetch(
    options: FetchOptions = {},
    onRefresh?: OnRefreshCallback<Playlist>
  ): Promise<SwrFetchResult> {
    const { forceRefresh = false, skipCache = false } = options;
    const startTime = performance.now();

    Logger.info(
      `Fetching playlist (forceRefresh=${forceRefresh}, skipCache=${skipCache})`,
      LOG_NAMESPACE
    );

    // Check if circuit is open - if so, return cached data immediately and skip network
    if (this.circuitBreaker.isOpen() && !forceRefresh) {
      const cacheResult = this.cacheGateway.get();
      if (cacheResult.data) {
        const durationMs = Math.round(performance.now() - startTime);
        Logger.info(
          `Circuit open - returning cached data without network request`,
          LOG_NAMESPACE
        );
        this.logFetchResult('cache', durationMs, cacheResult.data.tracks.length, true);

        return {
          data: cacheResult.data,
          source: 'cache',
          isStale: true,
          etag: cacheResult.etag,
          circuitOpen: true,
        };
      }
    }

    // Step 1: Check cache (unless skipCache or forceRefresh)
    if (!skipCache && !forceRefresh) {
      const cacheResult = this.cacheGateway.get();

      if (cacheResult.data) {
        Logger.info(
          `Cache hit: status=${cacheResult.status}, age=${cacheResult.age}ms`,
          LOG_NAMESPACE
        );

        // If data is fresh, return it without background refresh
        if (!cacheResult.isStale) {
          const durationMs = Math.round(performance.now() - startTime);
          this.logFetchResult('cache', durationMs, cacheResult.data.tracks.length, false);

          return {
            data: cacheResult.data,
            source: 'cache',
            isStale: false,
            etag: cacheResult.etag,
          };
        }

        // Data is stale - return it immediately but trigger background refresh
        const durationMs = Math.round(performance.now() - startTime);
        this.logFetchResult('cache', durationMs, cacheResult.data.tracks.length, true);

        // Start background refresh
        const backgroundRefresh = this.backgroundRefresh(cacheResult.etag, onRefresh);

        return {
          data: cacheResult.data,
          source: 'cache',
          isStale: true,
          etag: cacheResult.etag,
          backgroundRefresh,
        };
      }
    }

    // Step 2: No usable cache - fetch from network
    try {
      const result = await this.fetchFromNetwork();
      const durationMs = Math.round(performance.now() - startTime);
      this.logFetchResult('network', durationMs, result.data.tracks.length, false);

      return result;
    } catch (error) {
      // Step 3: Network failed - try cache fallback (unless skipCache)
      // Also handle CircuitOpenError gracefully
      const isCircuitOpen = error instanceof CircuitOpenError;

      if (!skipCache) {
        const cacheResult = this.cacheGateway.get();
        if (cacheResult.data) {
          const durationMs = Math.round(performance.now() - startTime);
          this.logFetchResult('fallback', durationMs, cacheResult.data.tracks.length, true, error);

          if (isCircuitOpen) {
            Logger.info(
              `Circuit open - returning cached data as fallback`,
              LOG_NAMESPACE
            );
          } else {
            Logger.warn(
              `Network failed, using stale cache as fallback: ${error instanceof Error ? error.message : String(error)}`,
              LOG_NAMESPACE
            );
          }

          return {
            data: cacheResult.data,
            source: 'fallback',
            isStale: true,
            etag: cacheResult.etag,
            circuitOpen: isCircuitOpen,
          };
        }
      }

      // No cache available - propagate error
      Logger.error(
        `Network failed and no cache available: ${error instanceof Error ? error.message : String(error)}`,
        LOG_NAMESPACE
      );
      throw error;
    }
  }

  /**
   * Force refresh from network, ignoring cache
   * Convenience method for explicit refresh
   */
  async forceRefresh(): Promise<FetchResult<Playlist>> {
    return this.fetch({ forceRefresh: true });
  }

  /**
   * Check if a fetch operation is currently in progress
   */
  isFetchInProgress(): boolean {
    return this.isFetching;
  }

  /**
   * Fetch from network with conditional request support
   *
   * @param etag - Optional ETag for If-None-Match header
   * @returns FetchResult with fresh data
   */
  private async fetchFromNetwork(etag?: string): Promise<FetchResult<Playlist>> {
    // Prevent duplicate concurrent fetches
    if (this.isFetching && this.currentFetchPromise) {
      Logger.info('Fetch already in progress, reusing existing request', LOG_NAMESPACE);
      return this.currentFetchPromise;
    }

    this.isFetching = true;
    this.currentFetchPromise = this.performFetch(etag);

    try {
      return await this.currentFetchPromise;
    } finally {
      this.isFetching = false;
      this.currentFetchPromise = null;
    }
  }

  /**
   * Perform the actual network fetch with circuit breaker protection
   */
  private async performFetch(etag?: string): Promise<FetchResult<Playlist>> {
    // Wrap network fetch with circuit breaker protection
    const result: ConditionalFetchResult<Playlist> = await this.circuitBreaker.execute(
      () => this.networkFetcher.fetch(etag)
    );

    // Handle 304 Not Modified
    if (result.notModified) {
      Logger.info('Server returned 304 Not Modified, using cached data', LOG_NAMESPACE);

      // Get cached data since server says it hasn't changed
      const cacheResult = this.cacheGateway.get();
      if (cacheResult.data) {
        // Update cache timestamp to mark it as fresh
        this.cacheGateway.set(cacheResult.data, { etag: result.etag });

        return {
          data: cacheResult.data,
          source: 'cache',
          isStale: false,
          etag: result.etag,
        };
      }

      // 304 but no cache - shouldn't happen, fall through to error
      throw new Error('Received 304 Not Modified but no cached data available');
    }

    // Fresh data received - update cache
    if (result.data) {
      this.cacheGateway.set(result.data, { etag: result.etag });

      return {
        data: result.data,
        source: 'network',
        isStale: false,
        etag: result.etag,
      };
    }

    throw new Error('Network fetch returned no data');
  }

  /**
   * Perform background refresh
   * Returns a promise that resolves when refresh completes
   * Handles circuit open gracefully - does not block or throw
   */
  private backgroundRefresh(
    etag?: string,
    onRefresh?: OnRefreshCallback<Playlist>
  ): Promise<FetchResult<Playlist>> {
    // Skip background refresh if circuit is open to avoid unnecessary attempts
    if (this.circuitBreaker.isOpen()) {
      Logger.info('Skipping background refresh - circuit is open', LOG_NAMESPACE);
      const cacheResult = this.cacheGateway.get();
      if (cacheResult.data) {
        const fallbackResult: FetchResult<Playlist> = {
          data: cacheResult.data,
          source: 'cache',
          isStale: true,
          etag: cacheResult.etag,
        };
        // Still invoke callback to signal "completion"
        setTimeout(() => onRefresh?.(fallbackResult), 0);
        return Promise.resolve(fallbackResult);
      }
    }

    Logger.info('Starting background refresh with SWR', LOG_NAMESPACE);

    // Create a promise for the background fetch
    const refreshPromise = this.fetchFromNetwork(etag)
      .then((result) => {
        Logger.info('Background refresh completed successfully', LOG_NAMESPACE);
        onRefresh?.(result);
        return result;
      })
      .catch((error) => {
        const isCircuitOpen = error instanceof CircuitOpenError;
        if (isCircuitOpen) {
          Logger.info(
            `Background refresh blocked by circuit breaker`,
            LOG_NAMESPACE
          );
        } else {
          Logger.warn(
            `Background refresh failed: ${error instanceof Error ? error.message : String(error)}`,
            LOG_NAMESPACE
          );
        }
        // Don't propagate error from background refresh - we already returned stale data
        // but still invoke callback with the cached data to signal completion
        const cacheResult = this.cacheGateway.get();
        if (cacheResult.data) {
          const fallbackResult: FetchResult<Playlist> = {
            data: cacheResult.data,
            source: 'fallback',
            isStale: true,
            etag: cacheResult.etag,
          };
          onRefresh?.(fallbackResult);
          return fallbackResult;
        }
        throw error;
      });

    return refreshPromise;
  }

  /**
   * Log fetch result using DataFetchLogger
   */
  private logFetchResult(
    source: 'network' | 'cache' | 'fallback',
    durationMs: number,
    itemCount: number,
    isStale: boolean,
    error?: unknown
  ): void {
    DataFetchLogger.logFetch({
      operation: 'playlist:swr-fetch',
      source: source === 'fallback' ? 'fallback' : source === 'cache' ? 'cache' : 'r2',
      status: error ? 'fallback' : 'success',
      durationMs,
      cacheHit: source === 'cache' || source === 'fallback',
      itemCount,
      errorMessage: error instanceof Error ? error.message : undefined,
      fallbackReason: isStale ? 'stale-cache' : undefined,
    });
  }
}

/**
 * Create a network fetcher adapter for R2MusicService
 * Adapts R2MusicService to PlaylistNetworkFetcher interface
 */
export function createR2NetworkFetcher(
  fetchFn: (etag?: string) => Promise<ConditionalFetchResult<Playlist>>
): PlaylistNetworkFetcher<Playlist> {
  return {
    fetch: fetchFn,
  };
}
